import { useLocation, Navigate } from 'react-router-dom';
import { Suspense } from 'react';

import lazy from '@lok/utils/lazyWithRetry';
import GlobalLoader from './Loading';
import useAuth from '../../hooks/useAuth';
import ScrollToTopWrapper from './ScrollWrapper';
import AllRoutes from '../../routes/AllRoutes';

const AuthLayout = lazy(() => import('./AuthLayout'));
const NotAuthLayout = lazy(() => import('./NotAuthLayout'));
const Layout = () => {
  const location = useLocation();
  const { authenticated, initializing } = useAuth();

  const NO_REDIRECT = [
    '/forgot-password',
    '/password/reset/confirm/',
    '/login'
  ];

  // Paths that must work in BOTH auth states: e.g. one-click unsubscribe pages reached
  // from email links — backend signed token is the source of authority, so the visitor
  // may or may not also have a session.
  const OPEN_PATHS = ['/email-preferences'];

  const isNoRedirectRoute =
    NO_REDIRECT.includes(location.pathname) ||
    location.pathname.startsWith(
      '/password/reset/confirm/'
    ) ||
    location.pathname.startsWith('/invite/');

  const isOpenPath = OPEN_PATHS.some((p) =>
    location.pathname.startsWith(p)
  );

  if (initializing) {
    return <GlobalLoader />;
  }

  if (!authenticated && !isNoRedirectRoute && !isOpenPath) {
    return (
      <Navigate
        to='/login'
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  if (authenticated && isNoRedirectRoute) {
    const redirectTo = location.state?.from;
    const safeRedirect = redirectTo && redirectTo !== '/login' ? redirectTo : '/dashboard';

    return <Navigate to={safeRedirect} replace />;
  }

  // Open paths render their own self-contained layout (own logo, own background).
  // Skip the auth/no-auth chrome wrapping so unauthenticated visitors hitting an
  // unsubscribe link don't see the sign-in video background behind their preferences.
  if (isOpenPath) {
    return (
      <ScrollToTopWrapper>
        <AllRoutes />
      </ScrollToTopWrapper>
    );
  }

  return (
    <Suspense
      fallback={
        <div className='flex flex-auto flex-col h-[100vh]'>
          <GlobalLoader />
        </div>
      }
    >
      <ScrollToTopWrapper>
        {authenticated ? <AuthLayout /> : <NotAuthLayout />}
      </ScrollToTopWrapper>
    </Suspense>
  );
};

export default Layout;
